document.addEventListener('DOMContentLoaded', () => {
  const rootUrl = window.Shopify?.routes?.root || '/';
  const menuLayer = document.querySelector('[data-menu-layer]');
  const menuDrawer = document.querySelector('[data-menu-drawer]');
  const menuButton = document.querySelector('[data-menu-open]');
  const menuCloseButtons = document.querySelectorAll('[data-menu-close]');
  const toast = document.querySelector('[data-cart-toast]');
  const toastMessage = document.querySelector('[data-cart-toast-message]');
  const toastClose = document.querySelector('[data-cart-toast-close]');
  let menuCloseTimer;
  let toastTimer;

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'} in cart`);
      bubble.classList.toggle('is-empty', count === 0);
    });
  };

  const showCartToast = (productTitle) => {
    if (!toast || !toastMessage) return;
    window.clearTimeout(toastTimer);
    toastMessage.textContent = `${productTitle} ${toast.dataset.addedMessage || 'has been added to your cart.'}`;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => {
        toast.hidden = true;
      }, 220);
    }, 4200);
  };

  const closeToast = () => {
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.classList.remove('is-visible');
    window.setTimeout(() => {
      toast.hidden = true;
    }, 220);
  };

  const openMenu = ({ focus = true } = {}) => {
    if (!menuLayer || !menuDrawer || !menuButton) return;
    window.clearTimeout(menuCloseTimer);
    menuLayer.hidden = false;
    menuDrawer.setAttribute('aria-hidden', 'false');
    menuButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-is-open');
    requestAnimationFrame(() => {
      menuLayer.classList.add('is-open');
      if (focus) menuDrawer.querySelector('[data-menu-close]')?.focus();
    });
  };

  const closeMenu = ({ restoreFocus = true } = {}) => {
    if (!menuLayer || !menuDrawer || !menuButton) return;
    menuLayer.classList.remove('is-open');
    menuDrawer.setAttribute('aria-hidden', 'true');
    menuButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-is-open');
    menuCloseTimer = window.setTimeout(() => {
      menuLayer.hidden = true;
    }, 260);
    if (restoreFocus) menuButton.focus();
  };

  menuButton?.addEventListener('click', openMenu);
  menuCloseButtons.forEach((button) => button.addEventListener('click', closeMenu));
  toastClose?.addEventListener('click', closeToast);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menuLayer?.classList.contains('is-open')) closeMenu();
  });

  if (window.Shopify?.designMode && menuLayer) {
    const matchesHeaderSection = (event) => event.detail?.sectionId === menuLayer.dataset.sectionId;

    document.addEventListener('shopify:section:select', (event) => {
      if (matchesHeaderSection(event)) openMenu({ focus: false });
    });

    document.addEventListener('shopify:block:select', (event) => {
      if (matchesHeaderSection(event)) openMenu({ focus: false });
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (matchesHeaderSection(event)) closeMenu({ restoreFocus: false });
    });
  }

  if (window.Shopify?.designMode) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented) return;

      const url = new URL(link.href, window.location.href);
      if (
        url.origin !== window.location.origin ||
        url.hash === '#' ||
        link.hasAttribute('download')
      ) return;

      event.preventDefault();
      const previewWindow = window.open(url.href, '_blank');
      if (previewWindow) previewWindow.opener = null;
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.method.toLowerCase() !== 'get') return;

      event.preventDefault();
      const url = new URL(form.action || window.location.href, window.location.href);
      new FormData(form).forEach((value, key) => url.searchParams.set(key, value));
      const previewWindow = window.open(url.href, '_blank');
      if (previewWindow) previewWindow.opener = null;
    });
  }

  document.querySelectorAll('[data-collection-sort]').forEach((select) => {
    select.addEventListener('change', () => select.form?.requestSubmit());
  });

  document.querySelectorAll('.js-product-form').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      if (!window.fetch) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const originalText = button?.textContent?.trim() || '';
      const productTitle = button?.dataset.productTitle || 'Product';

      if (button) {
        button.disabled = true;
        button.textContent = 'Adding…';
      }

      try {
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error('Unable to add item');

        const cartResponse = await fetch(`${rootUrl}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        const cart = await cartResponse.json();
        updateCartCount(cart.item_count);
        showCartToast(productTitle);

        if (button) button.textContent = 'Added ✓';
        window.setTimeout(() => {
          if (button) button.textContent = originalText;
        }, 1500);
      } catch (error) {
        form.submit();
      } finally {
        if (button) button.disabled = false;
      }
    });
  });

  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const variantsNode = section.querySelector('[data-product-variants]');
    if (!variantsNode) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch (error) {
      return;
    }

    const optionGroups = Array.from(section.querySelectorAll('[data-product-option]'));
    const variantInput = section.querySelector('[data-product-variant-input]');
    const buyVariantInput = section.querySelector('[data-buy-variant-input]');
    const price = section.querySelector('[data-product-price]');
    const submit = section.querySelector('.js-product-form [data-product-submit]');
    const buyNow = section.querySelector('[data-buy-submit]');
    const acceleratedCheckout = section.querySelector('[data-product-accelerated-checkout]');
    const gallery = section.querySelector('[data-product-gallery]');
    const mainImage = gallery?.querySelector('.product-media__image');

    const setInputValue = (input, value) => {
      if (!input) return;
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const renderPrice = (variant) => {
      if (!price || !variant) return;
      price.replaceChildren();

      const currentPrice = document.createElement('span');
      currentPrice.textContent = variant.price;
      price.append(currentPrice);

      if (variant.compareAtPrice) {
        const compareAtPrice = document.createElement('s');
        compareAtPrice.textContent = variant.compareAtPrice;
        price.append(compareAtPrice);
      }
    };

    const renderImage = (variant) => {
      if (!variant?.featuredImageUrl || !mainImage) return;
      mainImage.src = variant.featuredImageUrl;
      mainImage.srcset = '';
      mainImage.alt = variant.featuredImageAlt || '';

      gallery?.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
        const isActive = String(thumbnail.dataset.imageId) === String(variant.featuredImageId);
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-pressed', String(isActive));
      });
    };

    const renderAvailability = (variant) => {
      const isAvailable = Boolean(variant?.available);
      if (submit) {
        submit.disabled = !isAvailable;
        submit.textContent = !variant
          ? (submit.dataset.chooseLabel || 'Choose options')
          : (isAvailable ? submit.dataset.addLabel : submit.dataset.soldLabel);
      }
      if (buyNow) {
        buyNow.disabled = !isAvailable;
        const buyNowLabel = buyNow.querySelector('span');
        if (buyNowLabel) {
          buyNowLabel.textContent = !variant
            ? (buyNow.dataset.chooseLabel || 'Choose options')
            : (isAvailable ? buyNow.dataset.buyLabel : buyNow.dataset.soldLabel);
        }
      }
      if (acceleratedCheckout) acceleratedCheckout.hidden = !isAvailable;
    };

    const updateVariant = () => {
      const selectedOptions = optionGroups.map((group) => {
        const selected = group.querySelector('[data-option-value]:checked');
        const selectedLabel = group.querySelector('[data-selected-option-value]');
        if (selectedLabel) {
          selectedLabel.textContent = selected?.value || selectedLabel.dataset.emptyText || 'Not selected';
        }
        return selected?.value || '';
      });

      const variant = variants.find((candidate) => (
        candidate.options.length === selectedOptions.length
        && candidate.options.every((value, index) => String(value) === selectedOptions[index])
      ));

      if (!variant) {
        setInputValue(variantInput, '');
        setInputValue(buyVariantInput, '');
        renderAvailability(null);
        return;
      }

      setInputValue(variantInput, variant.id);
      setInputValue(buyVariantInput, variant.id);
      renderPrice(variant);
      renderAvailability(variant);
      renderImage(variant);

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (error) {
        // Variant selection still works when history updates aren't available.
      }
    };

    optionGroups.forEach((group) => {
      group.querySelectorAll('[data-option-value]').forEach((input) => {
        input.addEventListener('change', updateVariant);
      });
    });

    if (optionGroups.length) updateVariant();
  });

  const closeQuickVariant = (modal, { restoreFocus = true } = {}) => {
    if (!modal) return;
    const opener = modal._quickVariantOpener;
    modal.hidden = true;
    modal.removeAttribute('data-card-action');
    const hasOpenModal = Boolean(document.querySelector('[data-card-variant-modal]:not([hidden])'));
    document.documentElement.classList.toggle('quick-variant-is-open', hasOpenModal);
    document.body.classList.toggle('quick-variant-is-open', hasOpenModal);
    if (restoreFocus && opener instanceof HTMLElement) opener.focus();
  };

  const resetQuickVariant = (modal, action) => {
    modal.dataset.cardAction = action;
    modal.querySelectorAll('[data-card-option-value]').forEach((input) => {
      input.checked = false;
    });
    modal.querySelectorAll('[data-card-selected-value]').forEach((label) => {
      label.textContent = 'Not selected';
    });
    const status = modal.querySelector('[data-card-variant-status]');
    const confirm = modal.querySelector('[data-card-variant-confirm]');
    const buyInput = modal.querySelector('[data-card-buy-variant-input]');
    if (status) status.textContent = 'Please select all specifications';
    if (confirm) {
      confirm.disabled = true;
      confirm.textContent = 'Choose options';
    }
    if (buyInput) buyInput.value = '';
  };

  document.querySelectorAll('[data-card-variant-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const modal = document.getElementById(button.dataset.cardModalId || '');
      if (!modal) return;
      modal._quickVariantOpener = button;
      resetQuickVariant(modal, button.dataset.cardAction || 'add');
      if (modal.parentElement !== document.body) document.body.appendChild(modal);
      modal.hidden = false;
      document.documentElement.classList.add('quick-variant-is-open');
      document.body.classList.add('quick-variant-is-open');
      requestAnimationFrame(() => {
        modal.querySelector('.quick-variant__close')?.focus({ preventScroll: true });
      });
    });
  });

  document.querySelectorAll('[data-card-variant-modal]').forEach((modal) => {
    const variantsNode = modal.querySelector('[data-card-variants]');
    let variants = [];
    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
    } catch (error) {
      return;
    }

    const optionGroups = Array.from(modal.querySelectorAll('[data-card-option]'));
    const status = modal.querySelector('[data-card-variant-status]');
    const price = modal.querySelector('[data-card-variant-price]');
    const confirm = modal.querySelector('[data-card-variant-confirm]');
    const buyInput = modal.querySelector('[data-card-buy-variant-input]');
    const buyForm = modal.querySelector('.quick-variant__buy-form');

    const getSelectedVariant = () => {
      const selectedOptions = optionGroups.map((group) => {
        const selected = group.querySelector('[data-card-option-value]:checked');
        const selectedLabel = group.querySelector('[data-card-selected-value]');
        if (selectedLabel) selectedLabel.textContent = selected?.value || 'Not selected';
        return selected?.value || '';
      });

      if (selectedOptions.some((value) => !value)) return null;
      return variants.find((variant) => (
        variant.options.length === selectedOptions.length
        && variant.options.every((value, index) => String(value) === selectedOptions[index])
      )) || null;
    };

    const updateQuickVariant = () => {
      const variant = getSelectedVariant();
      if (buyInput) buyInput.value = variant?.id || '';
      if (price && variant?.price) price.textContent = variant.price;
      if (!variant) {
        if (status) status.textContent = 'Please select all specifications';
        if (confirm) {
          confirm.disabled = true;
          confirm.textContent = 'Choose options';
        }
        return;
      }

      if (status) status.textContent = variant.available ? 'In stock' : 'Unavailable';
      if (confirm) {
        const actionLabel = modal.dataset.cardAction === 'buy'
          ? (modal.dataset.buyLabel || 'Buy Now')
          : (modal.dataset.addLabel || 'Add Cart');
        confirm.disabled = !variant.available;
        confirm.textContent = variant.available
          ? actionLabel
          : 'Sold out';
      }
    };

    modal.querySelectorAll('[data-card-option-value]').forEach((input) => {
      input.addEventListener('change', updateQuickVariant);
    });
    modal.querySelectorAll('[data-card-variant-close]').forEach((button) => {
      button.addEventListener('click', () => closeQuickVariant(modal));
    });

    confirm?.addEventListener('click', async () => {
      const variant = getSelectedVariant();
      if (!variant?.available) return;

      if (modal.dataset.cardAction === 'buy') {
        if (buyInput) buyInput.value = variant.id;
        buyForm?.setAttribute('target', window.Shopify?.designMode ? '_blank' : '_top');
        buyForm?.submit();
        return;
      }

      const originalText = confirm.textContent;
      confirm.disabled = true;
      confirm.textContent = 'Adding…';
      try {
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
        });
        if (!response.ok) throw new Error('Unable to add item');
        const cartResponse = await fetch(`${rootUrl}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        const cart = await cartResponse.json();
        updateCartCount(cart.item_count);
        showCartToast(modal.dataset.productTitle || 'Product');
        closeQuickVariant(modal, { restoreFocus: false });
      } catch (error) {
        if (status) status.textContent = 'Please open the product page and try again.';
        confirm.disabled = false;
        confirm.textContent = originalText;
      }
    });
  });

  document.querySelectorAll('[data-product-thumbnail]').forEach((button) => {
    button.addEventListener('click', () => {
      const gallery = button.closest('[data-product-gallery]');
      const mainImage = gallery?.querySelector('.product-media__image');
      if (!mainImage || !button.dataset.imageSrc) return;

      mainImage.src = button.dataset.imageSrc;
      mainImage.srcset = '';
      mainImage.alt = button.dataset.imageAlt || '';

      gallery.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
        const isActive = thumbnail === button;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-pressed', String(isActive));
      });
    });
  });

  document.querySelectorAll('[data-product-quantity-step]').forEach((button) => {
    button.addEventListener('click', () => {
      const quantity = button.closest('.product-quantity')?.querySelector('input[name="quantity"]');
      if (!quantity) return;
      const current = Math.max(1, Number(quantity.value) || 1);
      quantity.value = button.dataset.productQuantityStep === 'increase'
        ? current + 1
        : Math.max(1, current - 1);
      const buyQuantityInput = document.querySelector('[data-buy-quantity-input]');
      if (buyQuantityInput) buyQuantityInput.value = quantity.value;
    });
  });

  document.querySelectorAll('.quantity-input[name="quantity"]').forEach((quantity) => {
    quantity.addEventListener('change', () => {
      quantity.value = Math.max(1, Number(quantity.value) || 1);
      const buyQuantityInput = document.querySelector('[data-buy-quantity-input]');
      if (buyQuantityInput) buyQuantityInput.value = quantity.value;
    });
  });

  document.querySelectorAll('[data-direct-checkout]').forEach((button) => {
    const externalFormId = button.getAttribute('form');
    const checkoutForm = externalFormId
      ? document.getElementById(externalFormId)
      : button.closest('form');
    checkoutForm?.setAttribute('target', window.Shopify?.designMode ? '_blank' : '_top');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector('[data-card-variant-modal]:not([hidden])');
    if (modal) closeQuickVariant(modal);
  });

  const cartForm = document.querySelector('[data-cart-form]');
  if (cartForm) {
    const currency = cartForm.dataset.currency || 'USD';
    const formatMoney = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    };

    const setLineLoading = (line, isLoading) => {
      if (!line) return;
      line.classList.toggle('is-loading', isLoading);
      line.querySelectorAll('button').forEach((button) => {
        button.disabled = isLoading;
      });
    };

    const renderCart = (cart) => {
      updateCartCount(cart.item_count);
      document.querySelectorAll('[data-cart-items-label]').forEach((label) => {
        label.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
      });
      const subtotal = document.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
      const shippingWrap = document.querySelector('[data-shipping-progress-wrap]');
      if (shippingWrap) {
        const threshold = Number(shippingWrap.dataset.shippingThreshold || 5900);
        const remaining = Math.max(0, threshold - cart.total_price);
        const progress = Math.min(100, (cart.total_price / threshold) * 100);
        const message = shippingWrap.querySelector('[data-shipping-message]');
        const bar = shippingWrap.querySelector('[data-shipping-progress-bar]');
        const track = shippingWrap.querySelector('[role="progressbar"]');
        const addLabel = shippingWrap.dataset.shippingAddLabel || 'Add';
        const addSuffix = shippingWrap.dataset.shippingAddSuffix || 'for free shipping';
        const unlockedLabel = shippingWrap.dataset.shippingUnlockedLabel || `You've unlocked free shipping`;
        if (message) {
          message.textContent = remaining > 0
            ? `${addLabel} ${formatMoney(remaining)} ${addSuffix}`
            : unlockedLabel;
        }
        if (bar) bar.style.width = `${progress}%`;
        if (track) track.setAttribute('aria-valuenow', String(Math.min(cart.total_price, threshold)));
      }

      const itemMap = new Map(cart.items.map((item) => [item.key, item]));
      document.querySelectorAll('[data-cart-line]').forEach((line) => {
        const item = itemMap.get(line.dataset.itemKey);
        if (!item) {
          line.classList.add('is-removing');
          window.setTimeout(() => line.remove(), 220);
          return;
        }
        const quantity = line.querySelector('[data-cart-quantity-value]');
        const lineTotal = line.querySelector('[data-cart-line-total]');
        if (quantity) quantity.textContent = item.quantity;
        if (lineTotal) lineTotal.textContent = formatMoney(item.final_line_price);
        setLineLoading(line, false);
      });

      if (cart.item_count === 0) window.location.reload();
    };

    const changeCartItem = async (key, quantity, line) => {
      setLineLoading(line, true);
      try {
        const response = await fetch(`${rootUrl}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!response.ok) throw new Error('Unable to update cart');
        renderCart(await response.json());
      } catch (error) {
        window.location.reload();
      }
    };

    cartForm.addEventListener('click', (event) => {
      const quantityButton = event.target.closest('[data-cart-quantity]');
      const removeButton = event.target.closest('[data-cart-remove]');
      const actionButton = quantityButton || removeButton;
      if (!actionButton) return;

      const line = actionButton.closest('[data-cart-line]');
      const currentQuantity = Number(line?.querySelector('[data-cart-quantity-value]')?.textContent || 0);
      const key = actionButton.dataset.itemKey;
      if (!line || !key) return;

      if (removeButton) {
        changeCartItem(key, 0, line);
        return;
      }

      const nextQuantity = quantityButton.dataset.action === 'increase'
        ? currentQuantity + 1
        : Math.max(0, currentQuantity - 1);
      changeCartItem(key, nextQuantity, line);
    });
  }

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
    const revealItems = document.querySelectorAll('.section, .trust-wrap, .why-us, .newsletter');
    revealItems.forEach((item) => item.classList.add('reveal'));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    revealItems.forEach((item) => observer.observe(item));
  }

  const prefetchedLinks = new Set();
  const prefetchInternalLink = (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (
      url.origin !== window.location.origin ||
      url.pathname === window.location.pathname ||
      url.pathname.startsWith('/checkout') ||
      url.pathname.startsWith('/account/logout') ||
      prefetchedLinks.has(url.href)
    ) return;

    prefetchedLinks.add(url.href);
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = url.href;
    document.head.appendChild(hint);
  };

  document.addEventListener('pointerover', prefetchInternalLink, { passive: true });
  document.addEventListener('touchstart', prefetchInternalLink, { passive: true });
});
